import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import BankingVisual from "@/components/landing/BankingVisual";

const HEADLINE = ["Banking-Infrastruktur", "für moderne", "Unternehmen."];

const lineVariant = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.25 + i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (d) => ({ opacity: 1, y: 0, transition: { duration: 0.7, delay: d, ease: [0.22, 1, 0.36, 1] } }),
};

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const visualY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const visualOpacity = useTransform(scrollYProgress, [0, 0.8], [1, 0.2]);

  return (
    <section
      id="top"
      ref={ref}
      className="relative overflow-hidden pt-28 lg:pt-36 pb-16 lg:pb-24"
      data-testid="hero-section"
    >
      {/* backgrounds */}
      <div className="absolute inset-0 grid-bg mask-radial" />
      <div className="absolute -top-40 left-1/4 h-[500px] w-[500px] rounded-full bg-[#e01e37]/10 blur-[120px]" />
      <div className="absolute top-20 right-0 h-[400px] w-[400px] rounded-full bg-[#e01e37]/5 blur-[140px]" />

      <div className="relative mx-auto max-w-[1400px] px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          {/* Left: copy */}
          <div className="lg:col-span-7">
            <motion.div
              variants={fadeUp}
              initial="hidden"
              animate="visible"
              custom={0}
              className="inline-flex items-center gap-3 border border-white/10 bg-white/[0.02] px-4 py-2 mb-8"
            >
              <span className="h-1.5 w-1.5 bg-[#e01e37] animate-pulse" />
              <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/60">
                Embedded Finance Plattform
              </span>
            </motion.div>

            <h1 className="font-display font-extrabold tracking-tight text-white leading-[0.98] text-4xl sm:text-5xl lg:text-[4.5rem]">
              {HEADLINE.map((line, i) => (
                <span key={i} className="block overflow-hidden">
                  <motion.span
                    className="block"
                    variants={lineVariant}
                    initial="hidden"
                    animate="visible"
                    custom={i}
                  >
                    {i === 1 ? (
                      <>
                        für <span className="text-[#e01e37] text-glow">moderne</span>
                      </>
                    ) : (
                      line
                    )}
                  </motion.span>
                </span>
              ))}
            </h1>

            <motion.p
              variants={fadeUp}
              initial="hidden"
              animate="visible"
              custom={0.7}
              className="mt-8 max-w-xl text-base lg:text-lg leading-relaxed text-white/60"
            >
              CashFUX verbindet Geschäftskonten, Wallets, Karten, Payments, Auszahlungen
              und APIs in einer zentralen White-Label-Plattform.
            </motion.p>

            <motion.div
              variants={fadeUp}
              initial="hidden"
              animate="visible"
              custom={0.9}
              className="mt-10 flex flex-col sm:flex-row items-stretch sm:items-center gap-4"
            >
              <a
                href="#plattform"
                data-testid="hero-primary-cta"
                className="group inline-flex items-center justify-center gap-2 bg-[#e01e37] px-7 py-4 text-sm font-medium text-white transition-[background-color] duration-300 hover:bg-[#c91830] accent-glow"
              >
                Plattform entdecken
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </a>
              <a
                href="#kontakt"
                data-testid="hero-secondary-cta"
                className="group inline-flex items-center justify-center gap-2 border border-white/20 px-7 py-4 text-sm font-medium text-white transition-colors duration-300 hover:bg-white/5"
              >
                Partner werden
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
            </motion.div>
          </div>

          {/* Right: visualization */}
          <motion.div
            style={{ y: visualY, opacity: visualOpacity }}
            className="lg:col-span-5"
          >
            <div className="relative border border-white/10 bg-[#0a0c12]/60 backdrop-blur-sm p-6 lg:p-8">
              <div className="mb-5 flex items-center justify-between">
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">
                  Netzwerk-Topologie
                </span>
                <span className="font-mono text-[10px] text-[#e01e37]">● live</span>
              </div>
              <BankingVisual />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
